'use client'

import { useRef, type KeyboardEvent } from 'react'
import { cx } from '@/app/lib/utils'
import type { TabsProps } from './Tabs.types'
import styles from './Tabs.module.scss'

/**
 * Tabs — navegación por pestañas con paneles asociados.
 *
 * Componente controlado: el estado vive fuera vía `value` + `onChange`.
 *
 * Accesibilidad: implementa el patrón WAI-ARIA `tabs`. El contenedor de
 * pestañas expone `role="tablist"` con `aria-label`; cada pestaña es un
 * `role="tab"` con `aria-selected` y `aria-controls` apuntando a su
 * `role="tabpanel"` (que a su vez se etiqueta con `aria-labelledby`). Usa
 * *roving tabindex* (solo la pestaña activa es tabulable) y navegación por
 * flechas (← →) e Inicio/Fin, que selecciona y mueve el foco. El foco visible
 * se delega al mixin `focus-ring` del DS.
 *
 * Con `pattern="navigation"` el mismo control cambia de semántica: `<nav>` +
 * botones con `aria-current`, sin paneles, sin *roving tabindex* y sin flechas
 * (ver {@link TabsPattern}). Es el modo para llevar el scroll a una sección de
 * la página, donde no hay `tabpanel` que anunciar.
 *
 * Client Component: gestiona eventos de teclado/ratón y referencias al DOM.
 */
export function Tabs<TValue extends string = string>({
  tabs,
  value,
  onChange,
  ariaLabel,
  width = 'intrinsic',
  pattern = 'tabs',
  tone = 'default',
  className,
}: TabsProps<TValue>) {
  const buttonsRef = useRef<Array<HTMLButtonElement | null>>([])

  if (tabs.length === 0) return null

  const isNavigation = pattern === 'navigation'

  /** Selecciona la pestaña de un índice y le mueve el foco. */
  function selectAt(index: number) {
    const tab = tabs[index]
    if (!tab || tab.disabled) return
    buttonsRef.current[index]?.focus()

    if (isNavigation || tab.value !== value) onChange(tab.value)
  }

  /** Avanza a la siguiente pestaña habilitada, con envolvente (wrap). */
  function moveFocus(fromIndex: number, direction: 1 | -1) {
    const total = tabs.length
    for (let step = 1; step <= total; step += 1) {
      const next = (fromIndex + direction * step + total * step) % total
      if (!tabs[next]?.disabled) {
        selectAt(next)
        return
      }
    }
  }

  function handleKeyDown(event: KeyboardEvent<HTMLButtonElement>, index: number) {
    switch (event.key) {
      case 'ArrowRight':
        event.preventDefault()
        moveFocus(index, 1)
        break
      case 'ArrowLeft':
        event.preventDefault()
        moveFocus(index, -1)
        break
      case 'Home':
        event.preventDefault()
        selectAt(tabs.findIndex((tab) => !tab.disabled))
        break
      case 'End': {
        event.preventDefault()
        for (let i = tabs.length - 1; i >= 0; i -= 1) {
          if (!tabs[i]?.disabled) {
            selectAt(i)
            break
          }
        }
        break
      }
      default:
        break
    }
  }

  const hasPanels = !isNavigation && tabs.some((tab) => tab.panel !== undefined)
  const listClassName = cx(
    styles.tablist,
    styles[width],
    tone === 'inverse' && styles.inverse,
    className,
  )

  const items = tabs.map((tab, index) => {
    const isSelected = tab.value === value
    // Roving tabindex solo en el patrón tabs: en una navegación todos los
    // destinos deben ser alcanzables con Tab.
    const rovingTabIndex = isSelected ? 0 : -1
    return (
      <button
        key={tab.value}
        ref={(node) => {
          buttonsRef.current[index] = node
        }}
        id={isNavigation ? undefined : `tab-${tab.value}`}
        type="button"
        role={isNavigation ? undefined : 'tab'}
        aria-selected={isNavigation ? undefined : isSelected}
        // En navegación no hay estado «seleccionado» sino «estás aquí».
        aria-current={isNavigation && isSelected ? 'true' : undefined}
        aria-controls={
          !isNavigation && tab.panel !== undefined ? `tabpanel-${tab.value}` : undefined
        }
        disabled={tab.disabled}
        tabIndex={isNavigation ? undefined : rovingTabIndex}
        className={cx(styles.tab, isSelected && styles.selected)}
        onClick={() => selectAt(index)}
        onKeyDown={isNavigation ? undefined : (event) => handleKeyDown(event, index)}
      >
        {tab.icon && (
          <span className={styles.icon} aria-hidden="true">
            {tab.icon}
          </span>
        )}
        <span>{tab.label}</span>
      </button>
    )
  })

  return (
    <>
      {isNavigation ? (
        <nav aria-label={ariaLabel} className={listClassName}>
          {items}
        </nav>
      ) : (
        <div role="tablist" aria-label={ariaLabel} className={listClassName}>
          {items}
        </div>
      )}

      {hasPanels &&
        tabs.map((tab) =>
          tab.panel !== undefined ? (
            <div
              key={tab.value}
              id={`tabpanel-${tab.value}`}
              role="tabpanel"
              aria-labelledby={`tab-${tab.value}`}
              hidden={tab.value !== value}
              tabIndex={0}
              className={styles.panel}
            >
              {tab.panel}
            </div>
          ) : null,
        )}
    </>
  )
}
